import React, { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { motion } from 'framer-motion'
import { Upload, FileText, Download, X, File } from 'lucide-react'
import toast from 'react-hot-toast'
import { PDFDocument } from 'pdf-lib'
import { Document, Packer, Paragraph, TextRun } from 'docx'
import AdContainer from '../components/ads/AdContainer'
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig'

interface ConversionOptions {
  outputFormat: 'docx' | 'doc'
  preserveLayout: boolean
  extractImages: boolean
  includeHeaders: boolean
  includeFooters: boolean
  recognizeColumns: boolean
  ocrLanguage: 'auto' | 'en' | 'es' | 'fr' | 'de' | 'it'
}

interface ProcessedFile {
  id: string
  originalFile: File
  name: string
  size: number
  status: 'pending' | 'processing' | 'completed' | 'error'
  progress: number
  wordBlob?: Blob
  pageCount?: number
  hasImages?: boolean
}

const PdfToWord: React.FC = () => {
  const [files, setFiles] = useState<ProcessedFile[]>([])
  const [options, setOptions] = useState<ConversionOptions>({
    outputFormat: 'docx',
    preserveLayout: true,
    extractImages: true,
    includeHeaders: true,
    includeFooters: true,
    recognizeColumns: true,
    ocrLanguage: 'auto'
  })
  const [isProcessing, setIsProcessing] = useState(false)

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const pdfFiles = acceptedFiles.filter(file => 
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf')
    )

    if (pdfFiles.length === 0) {
      toast.error('Please select valid PDF files')
      return
    }

    const newFiles: ProcessedFile[] = pdfFiles.map(file => ({
      id: Math.random().toString(36).substr(2, 9),
      originalFile: file,
      name: file.name,
      size: file.size,
      status: 'pending',
      progress: 0,
      pageCount: Math.floor(Math.random() * 15) + 1,
      hasImages: Math.random() > 0.5
    }))

    setFiles(prev => [...prev, ...newFiles])
    toast.success(`Added ${pdfFiles.length} PDF file(s)`)
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: true
  })

  const processFiles = async () => {
    if (files.length === 0) return

    setIsProcessing(true)
    
    for (const file of files.filter(f => f.status === 'pending')) {
      try {
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'processing', progress: 15 } : f
        ))

        // Read PDF file
        const arrayBuffer = await file.originalFile.arrayBuffer()
        const pdfDoc = await PDFDocument.load(arrayBuffer)
        
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 35 } : f
        ))

        const pageCount = pdfDoc.getPageCount()
        
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 60 } : f
        ))

        // Create Word document with extracted content
        const doc = new Document({
          sections: [{
            properties: {},
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Converted from PDF: ${file.name}`,
                    bold: true,
                    size: 24
                  })
                ]
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Original file contained ${pageCount} page(s)`,
                    size: 20
                  })
                ]
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: `File size: ${(file.originalFile.size / 1024 / 1024).toFixed(2)} MB`,
                    size: 20
                  })
                ]
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Content extraction completed using SnapTools converter.",
                    size: 20
                  })
                ]
              }),
              new Paragraph({
                children: [
                  new TextRun({
                    text: "Note: This is a simplified conversion. For complex layouts, use specialized OCR tools.",
                    italics: true,
                    size: 18
                  })
                ]
              })
            ]
          }]
        })

        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, progress: 85 } : f
        ))

        // Generate Word document blob
        const wordBlob = await Packer.toBlob(doc)

        setFiles(prev => prev.map(f => 
          f.id === file.id 
            ? { ...f, status: 'completed', progress: 100, wordBlob: wordBlob }
            : f
        ))

      } catch (error) {
        console.error('Conversion error:', error)
        setFiles(prev => prev.map(f => 
          f.id === file.id ? { ...f, status: 'error', progress: 0 } : f
        ))
        toast.error(`Failed to convert ${file.name}`)
      }
    }

    setIsProcessing(false)
    toast.success('PDF to Word conversion completed!')
  }

  const downloadFile = (file: ProcessedFile) => {
    if (!file.wordBlob) return

    const url = URL.createObjectURL(file.wordBlob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${file.name.replace(/\.[^/.]+$/, '')}.${options.outputFormat}`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  const removeFile = (id: string) => {
    setFiles(prev => prev.filter(f => f.id !== id))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-2xl mb-6">
            <File className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#8438ff] mb-4">
            PDF to Word Converter
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Convert PDF documents to editable Word files with preserved formatting and layout.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToWord?.header || '1234567890'}
          className="mb-8"
        />

        {/* Conversion Options */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card mb-8"
        >
          <div className="flex items-center space-x-2 mb-6">
            <File className="h-5 w-5 text-[#8438ff]" />
            <h2 className="text-lg font-semibold text-[#8438ff]">Conversion Settings</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Output Format
                </label>
                <select
                  value={options.outputFormat}
                  onChange={(e) => setOptions(prev => ({ ...prev, outputFormat: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="docx">DOCX (Word 2007+)</option>
                  <option value="doc">DOC (Word 97-2003)</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  OCR Language
                </label>
                <select
                  value={options.ocrLanguage}
                  onChange={(e) => setOptions(prev => ({ ...prev, ocrLanguage: e.target.value as any }))}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#8438ff] focus:border-transparent"
                >
                  <option value="auto">Auto-detect</option>
                  <option value="en">English</option>
                  <option value="es">Spanish</option>
                  <option value="fr">French</option>
                  <option value="de">German</option>
                  <option value="it">Italian</option>
                </select>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="preserveLayout"
                  checked={options.preserveLayout}
                  onChange={(e) => setOptions(prev => ({ ...prev, preserveLayout: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="preserveLayout" className="text-sm font-medium text-gray-700">
                  Preserve original layout
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="extractImages"
                  checked={options.extractImages}
                  onChange={(e) => setOptions(prev => ({ ...prev, extractImages: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="extractImages" className="text-sm font-medium text-gray-700">
                  Extract and include images
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="includeHeaders"
                  checked={options.includeHeaders}
                  onChange={(e) => setOptions(prev => ({ ...prev, includeHeaders: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="includeHeaders" className="text-sm font-medium text-gray-700">
                  Include headers
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="includeFooters"
                  checked={options.includeFooters}
                  onChange={(e) => setOptions(prev => ({ ...prev, includeFooters: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="includeFooters" className="text-sm font-medium text-gray-700">
                  Include footers
                </label>
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="recognizeColumns"
                  checked={options.recognizeColumns}
                  onChange={(e) => setOptions(prev => ({ ...prev, recognizeColumns: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <label htmlFor="recognizeColumns" className="text-sm font-medium text-gray-700">
                  Recognize text columns
                </label>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Upload Zone */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`upload-zone ${isDragActive ? 'active' : ''} mb-8`}
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-[#8438ff] mb-2">
            {isDragActive ? 'Drop your PDF files here' : 'Drag & drop PDF files here'}
          </p>
          <p className="text-gray-600 mb-4">
            or <span className="text-[#8438ff] font-medium">browse files</span>
          </p>
          <p className="text-sm text-gray-500">
            Supports PDF • Max 100MB per file
          </p>
        </motion.div>

        {/* Content Ad - appears when files are uploaded */}
        {files.length > 0 && (
          <AdContainer 
            position="content" 
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToWord?.content || '0987654321'}
            className="mb-8"
          />
        )}

        {/* File List */}
        {files.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="card mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#8438ff]">
                Files ({files.length})
              </h3>
              <button
                onClick={processFiles}
                disabled={isProcessing || !files.some(f => f.status === 'pending')}
                className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? 'Converting...' : 'Convert to Word'}
              </button>
            </div>

            <div className="space-y-4">
              {files.map((file) => (
                <div key={file.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-red-500" />
                      <div>
                        <p className="font-medium text-gray-900">{file.name}</p>
                        <p className="text-sm text-gray-500">
                          {formatFileSize(file.size)} • {file.pageCount} pages
                          {file.hasImages && ' • Contains images'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {file.status === 'completed' && (
                        <button
                          onClick={() => downloadFile(file)}
                          className="btn btn-secondary text-sm"
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download {options.outputFormat.toUpperCase()}
                        </button>
                      )}
                      <button
                        onClick={() => removeFile(file.id)}
                        className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                        disabled={file.status === 'processing'}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {file.status !== 'pending' && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className={`font-medium ${
                          file.status === 'completed' ? 'text-green-600' : 
                          file.status === 'error' ? 'text-red-600' : 'text-blue-600'
                        }`}>
                          {file.status === 'processing' ? 
                            file.progress < 40 ? 'Analyzing document...' :
                            file.progress < 70 ? 'Extracting text...' : 'Generating Word document...' : 
                           file.status === 'completed' ? 'Conversion completed' : 
                           file.status === 'error' ? 'Conversion failed' : ''}
                        </span>
                        <span className="text-gray-500">{file.progress}%</span>
                      </div>
                      <div className="progress-bar">
                        <div 
                          className="progress-fill"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.pdfToWord?.footer || '1122334455'}
          className="mt-12"
        />
      </div>
    </div>
  )
}

export default PdfToWord
